import type {
  FieldSpec,
  GroupSpec,
  QueryMetadata,
  QueryRequest,
  QueryRequestObjectAccessAction,
  SortSpec,
} from '@moment/horizon-data-access/platform'
import type {
  SormContext,
  SormEntityName,
  WhereInputOf,
} from '../gen/sorm-schema.generated'
import type {
  InferRow,
  TypedFieldSpec,
  TypedGroupSpec,
  TypedScopeEntry,
  TypedSortSpec,
} from './types'

import {
  createQueryRequest,
  executeSormQuery,
} from '@moment/horizon-data-access'

interface SormQueryState<Root extends SormEntityName> {
  rootEntity: Root
  context: SormContext
  fields: TypedFieldSpec<Root>[]
  filters?: WhereInputOf<Root>
  groups?: TypedGroupSpec<Root>[]
  scope?: TypedScopeEntry<Root>[]
  sort?: TypedSortSpec<Root>[]
  search?: string
  limit?: number
  offset?: number
  requestParams?: QueryRequest['request_params']
  objectAccessAction?: QueryRequestObjectAccessAction
  queryName?: string
}

/**
 * The subset of a SORM query that the bulk-selection order endpoints honor: the
 * query `context` plus an optional `filters` tree and free-text `search`. It
 * mirrors the backend's `pkgquery.QuerySpec` — those endpoints type their
 * `query` as exactly this shape and reject any other key (`root_entity`,
 * `fields`, `groups`, `sort`, pagination).
 */
export type SormSelectionQuery = {
  context: SormContext
  filters?: Record<string, unknown>
  search?: string
}

/**
 * A chainable SORM query. The `Named` flag records whether {@link
 * SormQueryBuilder.queryName} has been called: {@link SormQueryBuilder.execute}
 * and the react-query adapters accept only `Named = true`, so an unlabelled
 * query is a compile error rather than a request that is invisible in traces.
 * Every other chainable method preserves the flag.
 */
export class SormQueryBuilder<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[] = TypedFieldSpec<Root>[],
  Named extends boolean = false,
> {
  /**
   * Phantom marker carrying {@link Named}. TypeScript is structural, so a type
   * parameter that appears in no member is erased for assignability — without
   * this field a labelled and an unlabelled builder are the same type and the
   * `Named` gate on {@link execute} silently passes. Declared only, never
   * assigned, so it emits nothing at runtime.
   */
  private declare readonly namedMarker: Named

  private readonly state: SormQueryState<Root>
  private cachedRequest?: QueryRequest

  constructor(rootEntity: Root, context: SormContext) {
    this.state = {
      rootEntity,
      context,
      fields: [],
    }
  }

  fields<const NewF extends readonly TypedFieldSpec<Root>[]>(
    specs: NewF,
  ): SormQueryBuilder<Root, NewF, Named> {
    const next = this.clone<NewF>()
    next.state.fields = [...specs]
    return next
  }

  filter(where: WhereInputOf<Root>): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.filters = where
    return next
  }

  groupBy(specs: TypedGroupSpec<Root>[]): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.groups = specs
    return next
  }

  sortBy(specs: TypedSortSpec<Root>[]): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.sort = specs
    return next
  }

  scope(entries: TypedScopeEntry<Root>[]): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.scope = entries
    return next
  }

  context(ctx: SormContext): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.context = ctx
    return next
  }

  search(term: string): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.search = term
    return next
  }

  limit(n: number): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.limit = n
    return next
  }

  offset(n: number): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.offset = n
    return next
  }

  requestParams(
    params: QueryRequest['request_params'],
  ): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.requestParams = params
    return next
  }

  /**
   * Plan the root entity for `action` instead of view, so only rows the
   * caller may perform it on come back (`manage_access` on `Team`).
   */
  objectAccessAction(
    action: QueryRequestObjectAccessAction,
  ): SormQueryBuilder<Root, F, Named> {
    const next = this.clone<F>()
    next.state.objectAccessAction = action
    return next
  }

  /**
   * Tag this query with a snake_case label, surfaced as a no-op `?qn=`
   * URL param on the request. SORM queries all hit the same endpoint and show
   * up as `query` in network devtools; this label makes a given use-case
   * identifiable at a glance. The backend ignores the param.
   *
   * @param name - A use-case-specific snake_case label (e.g. `order_summary`).
   */
  queryName(name: string): SormQueryBuilder<Root, F, true> {
    const next = this.clone<F, true>()
    next.state.queryName = name
    return next
  }

  toRequest(): QueryRequest {
    if (this.cachedRequest) {
      return this.cachedRequest
    }
    this.cachedRequest = this.buildRequest()
    return this.cachedRequest
  }

  /**
   * Serialize only the subset a bulk-selection order endpoint honors —
   * `context`, `filters`, and `search`. Use this (not {@link toRequest}) when
   * building a bulk claim/approve/cancel request body: those endpoints type
   * their `query` as {@link SormSelectionQuery} and reject the full query
   * request's other keys (`root_entity`, `fields`, `groups`, `sort`,
   * pagination).
   */
  toSelectionQuery(): SormSelectionQuery {
    const query: SormSelectionQuery = { context: this.state.context }
    if (this.state.filters != null) {
      query.filters = this.state.filters as Record<string, unknown>
    }
    if (this.state.search != null) {
      query.search = this.state.search
    }
    return query
  }

  getQueryName(): string | undefined {
    return this.state.queryName
  }

  /**
   * The label, narrowed to `string`. The `Named = true` constraint on the
   * callers makes the throw unreachable from typed code; it guards untyped
   * (JS) callers rather than letting an unlabelled query reach the network.
   */
  requireQueryName(): string {
    if (this.state.queryName == null) {
      throw new Error(
        'SORM query is missing a queryName — call .queryName() before executing it.',
      )
    }
    return this.state.queryName
  }

  async execute(this: NamedSormQueryBuilder<Root, F>): Promise<{
    data: InferRow<Root, F>[]
    metadata: QueryMetadata | undefined
  }> {
    const request = this.toRequest()
    const response = await executeSormQuery(request, {
      queryName: this.requireQueryName(),
    })
    return {
      data: (response.data?.data ?? []) as InferRow<Root, F>[],
      metadata: response.data?.metadata,
    }
  }

  private buildRequest(): QueryRequest {
    const fields = this.state.fields.map((f) => this.mapFieldSpec(f))

    const request: Partial<QueryRequest> &
      Pick<QueryRequest, 'root_entity' | 'fields'> = {
      root_entity: this.state.rootEntity,
      context: this.state.context,
      fields,
    }

    if (this.state.filters != null) {
      request.filters = this.state.filters as Record<string, unknown>
    }

    if (this.state.groups != null) {
      request.groups = this.state.groups.map((g) => {
        const group: GroupSpec = {
          key: { entity: g.key.entity, field: g.key.field },
        }
        if (g.display_fields != null) {
          group.display_fields = g.display_fields.map((df) => ({
            entity: df.entity,
            field: df.field,
          }))
        }
        if (g.sort != null) {
          group.sort = g.sort.map((s) => this.mapSortSpec(s))
        }
        return group
      })
    }

    if (this.state.scope != null) {
      request.scope = this.state.scope.map((s) => ({
        entity: s.entity,
        field: s.field,
        value: s.value,
      }))
    }

    if (this.state.sort != null) {
      request.sort = this.state.sort.map((s) => this.mapSortSpec(s))
    }

    if (this.state.search != null) {
      request.search = this.state.search
    }
    if (this.state.limit != null) {
      request.limit = this.state.limit
    }
    if (this.state.offset != null) {
      request.offset = this.state.offset
    }
    if (this.state.requestParams != null) {
      request.request_params = this.state.requestParams
    }
    if (this.state.objectAccessAction != null) {
      request.object_access_action = this.state.objectAccessAction
    }

    return createQueryRequest(request)
  }

  private mapFieldSpec(f: TypedFieldSpec<Root>): FieldSpec {
    const spec: FieldSpec = {
      entity: f.entity,
      field: f.field,
    }
    if (f.aggregation != null) {
      spec.aggregation = f.aggregation
    }
    if (f.alias != null) {
      spec.alias = f.alias
    }
    if (f.weight_field != null) {
      spec.weight_field = f.weight_field
    }
    if (f.weight_entity != null) {
      spec.weight_entity = f.weight_entity
    }
    return spec
  }

  private mapSortSpec(s: TypedSortSpec<Root>): SortSpec {
    const spec: SortSpec = { field: s.field }
    if (s.entity != null) {
      spec.entity = s.entity
    }
    if (s.direction != null) {
      spec.direction = s.direction
    }
    return spec
  }

  private clone<
    NewF extends readonly TypedFieldSpec<Root>[],
    NewNamed extends boolean = Named,
  >(): SormQueryBuilder<Root, NewF, NewNamed> {
    const builder = new SormQueryBuilder<Root, NewF, NewNamed>(
      this.state.rootEntity,
      this.state.context,
    )
    builder.state.fields = [...this.state.fields]
    if (this.state.filters != null) {
      builder.state.filters = this.state.filters
    }
    if (this.state.groups != null) {
      builder.state.groups = [...this.state.groups]
    }
    if (this.state.scope != null) {
      builder.state.scope = [...this.state.scope]
    }
    if (this.state.sort != null) {
      builder.state.sort = [...this.state.sort]
    }
    if (this.state.search != null) {
      builder.state.search = this.state.search
    }
    if (this.state.limit != null) {
      builder.state.limit = this.state.limit
    }
    if (this.state.offset != null) {
      builder.state.offset = this.state.offset
    }
    if (this.state.requestParams != null) {
      builder.state.requestParams = this.state.requestParams
    }
    if (this.state.objectAccessAction != null) {
      builder.state.objectAccessAction = this.state.objectAccessAction
    }
    if (this.state.queryName != null) {
      builder.state.queryName = this.state.queryName
    }
    return builder
  }
}

/**
 * A {@link SormQueryBuilder} that carries a `queryName`, the only form
 * `execute()` and the react-query adapters accept. Prefer it over a bare `true`
 * third type argument when annotating a query factory's return type.
 */
export type NamedSormQueryBuilder<
  Root extends SormEntityName,
  F extends readonly TypedFieldSpec<Root>[] = TypedFieldSpec<Root>[],
> = SormQueryBuilder<Root, F, true>

export function sormQuery<E extends SormEntityName>(
  rootEntity: E,
  context: SormContext,
): SormQueryBuilder<E> {
  return new SormQueryBuilder(rootEntity, context)
}
